import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hasDefaultSheetName, readWorkbook } from "@/office/excel/workbook";

export const lektionAufbau: Lesson = {
  id: "aufbau",
  title: "Arbeitsmappe, Tabellenblatt, Zelle",
  summary: "Die drei Ebenen, auf denen in Excel alles stattfindet – und warum die mittlere die wichtigste ist.",
  durationMinutes: 8,
  learningGoals: [
    "Arbeitsmappe, Tabellenblatt und Zelle auseinanderhalten",
    "Ein neues Tabellenblatt anlegen und benennen",
    "Entscheiden, was auf ein eigenes Blatt gehört und was nicht",
  ],
  steps: [
    {
      id: "drei-ebenen",
      title: "Drei Ebenen, eine Datei",
      explanation: [
        "Was du speicherst, ist die Arbeitsmappe – eine einzige Datei mit der Endung " +
          ".xlsx. Darin liegen beliebig viele Tabellenblätter, und jedes Blatt besteht " +
          "aus einem Raster von Zellen. Alle drei Ebenen haben eigene Namen, weil sie " +
          "eigene Rechte haben: Schützen lässt sich jede einzeln, Formeln greifen über " +
          "Blattgrenzen hinweg, gespeichert wird nur als Ganzes.",
        "Die mittlere Ebene ist die, an der sich entscheidet, ob eine Datei nach einem " +
          "halben Jahr noch benutzbar ist. Die Faustregel dafür ist einfach und wird " +
          "trotzdem ständig gebrochen: Ein Blatt trägt genau eine Sache. Rohdaten auf " +
          "das eine Blatt, Auswertung auf das nächste, Einstellungen wie Steuersatz und " +
          "Stundenlohn auf ein drittes.",
        "Der Gegenentwurf ist das Blatt, auf dem drei Tabellen nebeneinanderstehen, " +
          "durch Leerspalten getrennt. Das sieht auf dem Bildschirm ordentlich aus und " +
          "kostet später jede Menge Zeit: Sortieren erwischt nur einen Teil, ein Filter " +
          "greift nicht über die Leerspalte hinweg, und ein Diagramm zieht sich " +
          "irgendwelche Zellen zusammen.",
      ],
      keyPoints: [
        "Arbeitsmappe = Datei, Tabellenblatt = Register unten, Zelle = ein Kästchen.",
        "Ein Blatt trägt genau eine Sache: Daten, Auswertung oder Einstellungen.",
        "Strg + Bild↓ und Strg + Bild↑ wechseln zwischen den Blättern.",
      ],
    },
    {
      id: "blatt-anlegen",
      title: "Ein zweites Blatt anlegen",
      explanation: [
        "Neue Blätter entstehen über das Pluszeichen rechts neben dem letzten Register " +
          "oder mit Umschalt + F11. Ein neues Blatt kostet nichts: Es vergrößert die " +
          "Datei kaum und ist jederzeit wieder löschbar.",
        "Für diesen Kurs brauchst du zwei Blätter – eines für die Daten, mit denen wir " +
          "durchgehend arbeiten, und eines für die Auswertungen, die in den späteren " +
          "Modulen dazukommen. Diese Trennung von Anfang an mitzudenken, ist die halbe " +
          "Miete.",
      ],
      keyPoints: [
        "Umschalt + F11 legt ein neues Tabellenblatt an.",
        "Rechtsklick auf ein Register: umbenennen, verschieben, kopieren, löschen.",
        "Blätter lassen sich per Ziehen am Register umsortieren.",
      ],
      task: {
        instruction: "Lege in deiner Arbeitsmappe ein zweites Tabellenblatt an.",
        hint: "Das Pluszeichen rechts neben dem letzten Blattregister – oder Umschalt + F11.",
        check: async () => {
          const mappe = await readWorkbook();
          if (mappe.sheets.length >= 2) {
            return pass(
              `Die Arbeitsmappe hat jetzt ${mappe.sheets.length} Blätter: ` +
                `${mappe.sheets.map((blatt) => `„${blatt.name}“`).join(", ")}.`,
            );
          }
          return fail(
            `Es gibt bisher nur ein Blatt („${mappe.sheets[0]?.name ?? "unbekannt"}“).`,
            "Klicke auf das Pluszeichen rechts neben dem Blattregister oder drücke Umschalt + F11.",
          );
        },
      },
    },
    {
      id: "blatt-benennen",
      title: "Blätter benennen",
      explanation: [
        "„Tabelle1“, „Tabelle2“, „Tabelle3“ – das sind keine Namen, das sind " +
          "Platznummern. Sie sagen nichts darüber, was auf dem Blatt steht, und werden " +
          "spätestens dann zum Problem, wenn eine Formel darauf verweist: " +
          "„=Tabelle3!B7“ zwingt jeden, der die Datei liest, zum Nachschlagen. " +
          "„=Einstellungen!B7“ erklärt sich von selbst.",
        "Ein guter Blattname ist kurz, ohne Leerzeichen und beschreibt den Inhalt: " +
          "„Umsatz“, „Rohdaten“, „Einstellungen“. Leerzeichen sind nicht verboten, aber " +
          "unpraktisch – Excel setzt einen Blattnamen mit Leerzeichen in Formeln in " +
          "Hochkommas, was Formeln unnötig lang macht.",
        "Umbenannt wird per Doppelklick auf das Register. Danach lohnt sich der zweite " +
          "Blick: Verweist bereits eine Formel auf das Blatt, passt Excel sie beim " +
          "Umbenennen automatisch an. Nur ein Blattname, der als Text in einer Zelle " +
          "steht, bleibt stehen – noch ein Grund, Namen nicht abzutippen.",
      ],
      keyPoints: [
        "Doppelklick auf das Register benennt ein Blatt um.",
        "Kurz, sprechend, ohne Leerzeichen – das spart in jeder Formel Platz.",
        "Beim Umbenennen zieht Excel alle Formelverweise automatisch mit.",
      ],
      task: {
        instruction:
          "Gib deinen Blättern sprechende Namen. Kein Blatt soll mehr „Tabelle1“, " +
          "„Tabelle2“ oder ähnlich heißen.",
        hint: "Doppelklick auf das Blattregister, neuen Namen eintippen, Eingabetaste.",
        check: async () => {
          const mappe = await readWorkbook();
          const standard = mappe.sheets.filter(hasDefaultSheetName);

          return fromCriteria(
            [
              criterion(
                `Mindestens zwei Tabellenblätter vorhanden (gefunden: ${mappe.sheets.length})`,
                mappe.sheets.length >= 2,
              ),
              criterion(
                standard.length === 0
                  ? "Alle Blätter tragen einen selbst vergebenen Namen"
                  : `Noch mit Standardnamen: ${standard.map((blatt) => `„${blatt.name}“`).join(", ")}`,
                standard.length === 0,
              ),
            ],
            {
              success: `Gut benannt: ${mappe.sheets.map((blatt) => `„${blatt.name}“`).join(", ")}.`,
              failure: "Da trägt noch mindestens ein Blatt seinen Standardnamen.",
              hint: "Doppelklick auf das Register. Ein Name wie „Umsatz“ oder „Einstellungen“ sagt mehr als „Tabelle2“.",
            },
          );
        },
      },
    },
  ],
};
